import Vue from "vue";
import VueRouter from "vue-router";
import { apolloClient } from "@/apollo/apollo";
import { getAuthenticationState, myProjects, getProject } from "@/graphql/queries";
import { updateAuthenticationState } from "./graphql/mutations";

import { auth } from "./firebaseConfig";

Vue.use(VueRouter);

import AuthRoutes from "./modules/Auth/routes";
import DashboardRoutes from "./modules/Dashboard/routes";
import OnboardingRoutes from "./modules/Onboarding/routes";
import InvitesRoutes from "./modules/Invites/routes";

const routes = [
  ...AuthRoutes,
  ...OnboardingRoutes,
  ...InvitesRoutes,
  {
    name: "Root",
    path: "/",
    meta: {
      requiresAuth: true,
      requiresOnboard: true,
    },
    component: () =>
      import(
        /* webpackChunkName: "BaseModule" */ "./modules/Layout/MainLayout.vue"
      ),
    children: [...DashboardRoutes],
  },
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes,
});

router.beforeEach(async (to, from, next) => {

  let authState = await apolloClient.query({
    query: getAuthenticationState,
  });

  const requiresAuth = to.matched.some((x) => x.meta.requiresAuth);
  const requiresGuest = to.matched.some((x) => x.meta.requiresGuest);
  const requiresOnboard = to.matched.some((x) => x.meta.requiresOnboard);
  const requiresOwner = to.matched.some((x) => x.meta.requiresOwner);
  const requiresPaymentMethod = to.matched.some((x) => x.meta.requiresPaymentMethod);

  const loggedIn = auth.currentUser;

  let selectedProject = authState.data.authenticationState.selectedProject;

  let isOnboarded = !authState.data.authenticationState.needsToOnboard;

  if (requiresAuth && !loggedIn) {
    return next({
      name: "Login",
      query: {
        ...to.query,
      },
    });
  }

  if (to.query.redirectTo && loggedIn) {
    return next(to.query.redirectTo);
  }

  if (requiresGuest && loggedIn) {
    return next({
      name: "Project",
      query: {
        ...to.query,
        project: selectedProject
      },
    });
  }

  if (requiresOnboard && !isOnboarded) {
    return next({
      name: "Onboarding"
    });
  }

  if (requiresPaymentMethod) {

    let currentProject = await apolloClient.query({
      query: getProject,
      variables: {
        input: {
          projectId: selectedProject,
        },
      },
    });

    if (!currentProject.data.project.project.billing.hasDefaultPayment) {
      return next({
        name: 'NeedsPaymentMethod',
        query: {
          ...to.query,
          project: selectedProject
        }
      });
    }

  }

  if (requiresOwner) {
    let currentProject = await apolloClient.query({
      query: getProject,
      variables: {
        input: {
          projectId: selectedProject,
        },
      },
    });

    const teamMembers = currentProject.data.project.project.team.map(
      (member) => {
        return { role: member.role, ...member.user };
      }
    );

    const myRole = teamMembers.reduce((role, member) => {
      if (member.id === authState.data.authenticationState.uid) {
        role = member.role;
      }

      return role;
    }, "member");

    if (myRole !== "owner") {
      return next({
        name: "Project",
        query: {
          ...to.query,
          project: selectedProject
        },
      });
    }
  }

  if (to.query.project && to.query.project !== selectedProject && loggedIn) {
    await apolloClient.mutate({
      mutation: updateAuthenticationState,
      variables: { selectedProject: to.query.project },
    });
  }

  if (!to.query.project && isOnboarded && loggedIn) {

    return next({
      name: to.name,
      params: to.params,
      query: {
        ...to.query,
        project: selectedProject
      }
    });
  }

  return next();
});

export default router;
